import { useMemo } from "react"
import type { ConditionGroup, NextPageParams, PaginationMetadata, Task, TaskVersionsResponse } from "@/schema"
import { useDataTableStore } from "@/stores/data-table-store"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import {
  AllTaskIdsResponse,
  fetchAllTaskIds,
  fetchBusinessTasks,
  fetchContactTasks,
  fetchDashboardTasks,
  fetchGroupedTasks,
  fetchHouseholdTaskCounts,
  fetchHouseholdTasks,
  fetchPersonTasks,
  fetchProjectTasks,
  fetchTask,
  fetchTaskApprovalCounts,
  fetchTaskQueue,
  fetchTasksWithDateRange,
  fetchTaskVersion,
  fetchTaskVersions,
  fetchTrustTasks,
  GroupedTasksResponse,
  TaskApprovalCountsResponse,
  TaskMeetingScope,
  TaskQueueResponse,
} from "./api"

export const groupKeyMappings = {
  pending: "Pending",
  due_today: "Today",
  due_this_week: "This week",
  due_after_this_week: "Upcoming",
  no_due_date: "No due date",
  done: "Done",
}

const ENTITY_TASKS_PAGE_SIZE = 25
const INITIAL_ENTITY_TASKS_PAGE = { page: 1, per_page: ENTITY_TASKS_PAGE_SIZE }

function flattenTaskPages(data: { pages: { tasks: Task[]; pagination?: PaginationMetadata }[] }) {
  return {
    tasks: data.pages.flatMap((page) => page.tasks),
    pagination: data.pages[data.pages.length - 1]?.pagination,
  }
}

export function useFetchHouseholdTaskCounts(householdId: string) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["tasks", "household", householdId, "counts"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchHouseholdTaskCounts(context, householdId)
    },
    enabled: !!householdId,
  })
}

export function useFetchHouseholdTasks(
  householdId: string,
  search?: string,
  projectId?: string,
  includeRecommended?: boolean,
  assignedTo?: string[],
  withoutProject?: boolean,
  viewFilterGroup?: ConditionGroup
) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: [
      "tasks",
      "household",
      householdId,
      search || undefined,
      projectId || undefined,
      includeRecommended || undefined,
      assignedTo,
      withoutProject || undefined,
      viewFilterGroup,
    ],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchHouseholdTasks(
        context,
        householdId,
        search,
        projectId,
        includeRecommended,
        assignedTo,
        withoutProject,
        viewFilterGroup
      )
    },
    enabled: !!householdId,
  })
}

export function useInfiniteHouseholdTasks(
  householdId: string,
  search?: string,
  projectId?: string,
  includeRecommended?: boolean,
  assignedTo?: string[],
  withoutProject?: boolean,
  viewFilterGroup?: ConditionGroup,
  viewSort?: { field?: string; direction?: "asc" | "desc" }
) {
  const getContext = useApiContext()
  return useInfiniteQuery({
    queryKey: [
      "tasks",
      "household",
      householdId,
      "infinite",
      search || undefined,
      projectId || undefined,
      includeRecommended || undefined,
      assignedTo,
      withoutProject || undefined,
      viewFilterGroup,
      viewSort,
    ],
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchHouseholdTasks(
        context,
        householdId,
        search,
        projectId,
        includeRecommended,
        assignedTo,
        withoutProject,
        viewFilterGroup,
        pageParam,
        viewSort
      )
    },
    initialPageParam: INITIAL_ENTITY_TASKS_PAGE,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_page_params ?? undefined,
    select: flattenTaskPages,
    enabled: !!householdId,
  })
}

export function useFetchProjectTasks(projectId: string, search?: string) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["tasks", "project", projectId, search],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchProjectTasks(context, projectId, search)
    },
    enabled: !!projectId,
  })
}

export function useFetchContactTasks(contactId: string, search?: string) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["tasks", "contact", contactId, search],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchContactTasks(context, contactId, search)
    },
    enabled: !!contactId,
  })
}

export function useInfiniteContactTasks(
  contactId: string,
  search?: string,
  viewSort?: { field?: string; direction?: "asc" | "desc" }
) {
  const getContext = useApiContext()
  return useInfiniteQuery({
    queryKey: ["tasks", "contact", contactId, "infinite", search, viewSort],
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchContactTasks(context, contactId, search, pageParam, viewSort)
    },
    initialPageParam: INITIAL_ENTITY_TASKS_PAGE,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_page_params ?? undefined,
    select: flattenTaskPages,
    enabled: !!contactId,
  })
}

export function useFetchBusinessTasks(businessId: string, search?: string) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["tasks", "business", businessId, search],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchBusinessTasks(context, businessId, search)
    },
    enabled: !!businessId,
  })
}

export function useInfiniteBusinessTasks(
  businessId: string,
  search?: string,
  viewSort?: { field?: string; direction?: "asc" | "desc" }
) {
  const getContext = useApiContext()
  return useInfiniteQuery({
    queryKey: ["tasks", "business", businessId, "infinite", search, viewSort],
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchBusinessTasks(context, businessId, search, pageParam, viewSort)
    },
    initialPageParam: INITIAL_ENTITY_TASKS_PAGE,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_page_params ?? undefined,
    select: flattenTaskPages,
    enabled: !!businessId,
  })
}

export function useFetchTrustTasks(trustId: string, search?: string) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["tasks", "trust", trustId, search],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchTrustTasks(context, trustId, search)
    },
    enabled: !!trustId,
  })
}

export function useFetchPersonTasks(personId: string, search?: string) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["tasks", "person", personId, search],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchPersonTasks(context, personId, search)
    },
    enabled: !!personId,
  })
}

export function useFetchTasksWithDateRange(startDate?: string, endDate?: string) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["date-range-tasks", "date-range", startDate, endDate],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchTasksWithDateRange(context, startDate, endDate)
    },
  })
}

export function useFetchTask(id: string, options?: { enabled?: boolean }) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["task", id],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchTask(context, id)
    },
    enabled: options?.enabled !== undefined ? options.enabled : true,
  })
}

export function useFetchTaskVersion(taskId: string, versionId?: string) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["task-version", taskId, versionId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      if (!versionId) throw new Error("Task version id is required")
      return fetchTaskVersion(context, taskId, versionId)
    },
    enabled: Boolean(taskId && versionId),
  })
}

export function useInfiniteTaskVersions(taskId: string) {
  const getContext = useApiContext()

  return useInfiniteQuery<
    TaskVersionsResponse,
    Error,
    {
      current: TaskVersionsResponse["current"] | undefined
      versions: TaskVersionsResponse["versions"]
    },
    [string, string],
    NextPageParams | undefined
  >({
    queryKey: ["task-versions", taskId],
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchTaskVersions(context, taskId, pageParam)
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      current: data.pages[0]?.current,
      versions: data.pages.flatMap((page) => page.versions),
    }),
    enabled: Boolean(taskId),
  })
}

export function useInfiniteTaskQueue(
  status: "pending" | "rejected",
  filterGroup?: ConditionGroup,
  meetingScope: TaskMeetingScope = "my_meetings"
) {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useInfiniteQuery<
    TaskQueueResponse,
    Error,
    { tasks: Task[]; pagination?: PaginationMetadata },
    (string | ConditionGroup | undefined)[],
    NextPageParams | undefined
  >({
    queryKey: ["tasks", "queue", status, bookId, filterGroup, meetingScope],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchTaskQueue(context, status, pageParam, filterGroup, meetingScope)
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_page_params ?? undefined,
    select: (data) => ({
      tasks: data.pages.flatMap((page) => page.tasks),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
  })
}

export function useTaskApprovalCounts(enabled: boolean = true) {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useQuery<TaskApprovalCountsResponse>({
    queryKey: ["tasks", "approval_counts", bookId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchTaskApprovalCounts(context)
    },
    enabled,
  })
}

export function useInfiniteGroupedTasks(enabled?: boolean) {
  const getContext = useApiContext()
  const bookId = useBookId()
  const { completedConditions, searchTerm, viewSort } = useDataTableStore()
  const viewerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const computedFilterGroup = useMemo(
    () => ({ combinator: "and", conditions: completedConditions }) as ConditionGroup,
    [completedConditions]
  )

  return useInfiniteQuery<
    GroupedTasksResponse,
    Error,
    Task[],
    (string | ConditionGroup | undefined | { field?: string; direction?: "asc" | "desc" })[],
    NextPageParams | undefined
  >({
    queryKey: ["tasks", "grouped", searchTerm, computedFilterGroup, viewSort, viewerTimezone, bookId],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchGroupedTasks(context, computedFilterGroup, searchTerm, pageParam, viewSort, viewerTimezone)
    },
    initialPageParam: undefined,
    enabled,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => {
      const mergedGroups: Record<string, { tasks: Task[]; total_count: number }> = {}

      data.pages.forEach((page) => {
        Object.entries(page.groups).forEach(([groupKey, groupData]) => {
          if (!mergedGroups[groupKey]) {
            mergedGroups[groupKey] = { tasks: [], total_count: groupData.total_count }
          }
          mergedGroups[groupKey].tasks.push(...groupData.tasks)
        })
      })

      return Object.entries(mergedGroups).flatMap(([groupKey, groupData]) =>
        groupData.tasks.map((task) => ({
          ...task,
          groupKey,
          groupKeyTotalCount: groupData.total_count,
        }))
      )
    },
  })
}

const dashboardFilterGroup: ConditionGroup = {
  combinator: "and",
  conditions: [
    {
      entity_type: "task",
      field: "assigned_to_id",
      label: "Assigned user",
      operator: "in",
      type: "Conditions::AssignedToUserCondition",
      values: ["current_user_id"],
    },
    {
      entity_type: "task",
      field: "status",
      label: "Status",
      operator: "eq",
      type: "Conditions::EnumCondition",
      value: "todo",
    },
  ],
}

export type DashboardTaskSort = "due_today" | "most_overdue" | "highest_priority" | "lowest_priority"

const dashboardSortMap: Record<DashboardTaskSort, { field: string; direction: "asc" | "desc" }> = {
  due_today: { field: "deadline_at", direction: "desc" },
  most_overdue: { field: "deadline_at", direction: "asc" },
  highest_priority: { field: "priority", direction: "desc" },
  lowest_priority: { field: "priority", direction: "asc" },
}

export function useFetchDueTodayTasks(sort: DashboardTaskSort = "due_today", enabled: boolean = true) {
  const getContext = useApiContext()
  const bookId = useBookId()
  const viewerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const viewSort = dashboardSortMap[sort]

  return useQuery<Task[]>({
    queryKey: ["tasks", "dashboard", bookId, "due-today", sort, viewerTimezone],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      const response = await fetchDashboardTasks(context, dashboardFilterGroup, viewSort, {
        taskGroup: "due_today",
        timezone: viewerTimezone,
      })
      return response.tasks ?? []
    },
    enabled,
  })
}

export function useFetchAllTaskIds(enabled?: boolean) {
  const getContext = useApiContext()
  const { searchTerm, completedConditions } = useDataTableStore()
  const computedFilterGroup = useMemo(
    () => ({ combinator: "and", conditions: completedConditions }) as ConditionGroup,
    [completedConditions]
  )

  return useQuery<AllTaskIdsResponse>({
    queryKey: ["tasks", "all_ids", searchTerm, computedFilterGroup],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchAllTaskIds(context, computedFilterGroup, searchTerm)
    },
    enabled,
  })
}
